/** Summary surfaces: metrics, recommendation cards, the impact header. */
import type { ReactNode } from 'react'
import type { Money, Recommendation } from '@/generated/types'
import { t, type CopyKey } from '@/lib/copy'
import { formatMoney } from '@/lib/format'
import { displayParams, effortName } from '@/lib/display'
import { moneyColorVar } from '@/lib/tokens'
import { GeneratedText } from './GeneratedText'
import { Badge } from './primitives'
import { IconChevron } from './icons'

export interface MetricSpec {
  key: string
  label: string
  value: string
  note?: string
  tone?: 'default' | 'positive' | 'negative' | 'caution'
}

export function MetricRow({ metrics }: { metrics: MetricSpec[] }) {
  return (
    <div className="metric-row">
      {metrics.map((metric) => (
        <div className="metric" key={metric.key}>
          <span className="metric-label">{metric.label}</span>
          <span
            className={`metric-value${metric.tone && metric.tone !== 'default' ? ` ${metric.tone}` : ''}`}
          >
            {metric.value}
          </span>
          {metric.note ? <span className="metric-note">{metric.note}</span> : null}
        </div>
      ))}
    </div>
  )
}

/** Money rendered with the sign convention: money in positive, money out red. */
export function Amount({
  money,
  absolute,
  signed,
  colored = true,
}: {
  money: Money | null | undefined
  absolute?: boolean
  signed?: boolean
  colored?: boolean
}) {
  return (
    <span
      className="numeric"
      style={colored && money ? { color: moneyColorVar(money.amount) } : undefined}
    >
      {formatMoney(money, { absolute, signed })}
    </span>
  )
}

export function SummaryCard({ children, raised }: { children: ReactNode; raised?: boolean }) {
  return <div className={`card card-pad-lg${raised ? ' card-raised' : ''}`}>{children}</div>
}

export function DetailRows({ children }: { children: ReactNode }) {
  return <dl className="detail-rows">{children}</dl>
}

export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="detail-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/**
 * One prioritized action. Title and rationale come from the model when it
 * wrote them and from the copyKey template when it did not — which is the
 * whole card in a deploy with generation off, and is complete either way.
 * `recommendation.copyKey` + `params` stay required precisely so this works.
 */
export function RecommendationCard({
  recommendation,
  onSelect,
}: {
  recommendation: Recommendation
  onSelect: () => void
}) {
  const params = displayParams(recommendation.params)
  const monthly = recommendation.monthlyImpact
  const oneTime = recommendation.oneTimeImpact
  const impact =
    monthly && monthly.amount !== 0
      ? t('recommendations.card.impact', { amount: formatMoney(monthly, { absolute: true }) })
      : oneTime && oneTime.amount !== 0
        ? t('recommendations.card.impactOnce', { amount: formatMoney(oneTime, { absolute: true }) })
        : null

  return (
    <button type="button" className="rec-card" onClick={onSelect}>
      <div className="rec-card-main">
        <GeneratedText
          text={recommendation.prose?.title}
          copyKey={`${recommendation.copyKey}.title` as CopyKey}
          params={params}
          as="span"
          className="rec-card-title"
        />
        <GeneratedText
          text={recommendation.prose?.why}
          copyKey={`${recommendation.copyKey}.why` as CopyKey}
          params={params}
          as="span"
          className="rec-card-why"
        />

        <span className="row" style={{ gap: 'var(--space-xs)' }}>
          <Badge>{effortName(recommendation.effort)}</Badge>
          {recommendation.status === 'done' ? (
            <Badge tone="positive">{t('recommendations.badge.done')}</Badge>
          ) : null}
          {recommendation.status === 'dismissed' ? (
            <Badge>{t('recommendations.badge.dismissed')}</Badge>
          ) : null}
        </span>
      </div>
      {impact ? <span className="rec-card-impact">{impact}</span> : null}
      <IconChevron size={18} />
    </button>
  )
}

export function ImpactCard({ items }: { items: { key: string; label: string; value: string }[] }) {
  return (
    <div className="impact-card">
      {items.map((item) => (
        <div key={item.key} className="stack-sm" style={{ gap: 'var(--space-xxs)' }}>
          <span className="metric-label">{item.label}</span>
          <span className="impact-value">{item.value}</span>
        </div>
      ))}
    </div>
  )
}
